import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Modal,
  Typography,
  Grid,
  Menu,
  MenuItem,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useActivities } from "../../state/ActivityContext";
import { ClientContext } from "../../state/ClientContext";
import { addBreadcrumb } from "../../config/sentry";

const ActionBtn = ({
  actionMode,
  handleSave,
  isSaving,
  enableDeleteOptions,
}) => {
  const navigate = useNavigate();
  const { deleteActivity, selectedActivity } = useActivities();
  const { selectedClient } = useContext(ClientContext);
  const [open, setOpen] = useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [deleteTypeOption, setDeleteTypeOption] = useState(null);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleDelete = async () => {
    try {
      if (!selectedActivity?.id) {
        console.error("No activity selected to delete");
        return;
      }
      await deleteActivity("single");
      setOpen(false);
      navigate(`/calendar/${selectedClient?.id}`);
    } catch (error) {
      console.error("Failed to delete the activity:", error);
    }
  };

  const handleDeleteAll = async () => {
    try {
      if (!selectedActivity?.id) {
        console.error("No activity selected to delete series");
        return;
      }
      await deleteActivity("all");
      setOpen(false);
      navigate(`/calendar/${selectedClient?.id}`);
    } catch (error) {
      console.error("Failed to delete the activity:", error);
    }
  };

  const handleLeave = () => {
    setOpenCancelModal(false);
    navigate(`/calendar/${selectedClient?.id}`);
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const handleDeleteClick = (event) => {
    setAnchorEl(event.currentTarget);
    setDeleteMenuOpen(true);
  };

  const [deleteMenuOpen, setDeleteMenuOpen] = useState(false);
  const handleCloseDeleteMenu = () => {
    setAnchorEl(null);
    setDeleteMenuOpen(false);
  };

  return (
    <>
      <div style={{ width: "100%" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {/* Delete Button */}
          {actionMode === "Add New" ? (
            <div></div>
          ) : (
            <Button
              variant="outlined"
              onClick={enableDeleteOptions ? handleDeleteClick : handleOpen}
              className="activity-action-btn"
            >
              <Typography textTransform={"none"}>
                Delete activity
                {enableDeleteOptions && <KeyboardArrowDownIcon />}
              </Typography>
            </Button>
          )}

          {/* Cancel and Save Buttons */}
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              onClick={() => {
                setOpenCancelModal(true);
              }}
              className="activity-action-btn"
              disabled={isSaving}
              aria-controls={open ? "delete-options-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={deleteMenuOpen ? "true" : undefined}
            >
              <Typography textTransform={"capitalize"}>Cancel</Typography>
            </Button>
            <Button
              variant="outlined"
              className="activity-action-btn"
              onClick={handleSave}
              disabled={isSaving}
            >
              <Typography textTransform={"capitalize"}>Save</Typography>
            </Button>
          </Box>
        </Box>
      </div>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="delete-activity-modal"
        aria-describedby="delete-activity-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            boxShadow: 24,
            borderRadius: 2,
            backgroundColor: "#d0d0d0",
            color: "#000000",
            p: 3,
          }}
        >
          <Typography
            id="delete-activity-modal"
            variant="h6"
            mb={6}
            style={{ textAlign: "center", color: "#000000" }}
          >
            <strong>
              {!enableDeleteOptions
                ? "Are you sure you want to delete this activity from your client's schedule?"
                : deleteTypeOption === "all"
                  ? "Are you sure you want to delete this series from your client's schedule?"
                  : "Are you sure you want to delete the single occurrence of this activity?"}
            </strong>
          </Typography>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item>
              <Button
                variant="outlined"
                className="activity-action-btn"
                onClick={handleClose}
              >
                <Typography textTransform={"capitalize"}>Go Back </Typography>
              </Button>
            </Grid>
            {
              <Grid item>
                <Button
                  variant="contained"
                  color="error"
                  onClick={
                    deleteTypeOption === "all" ? handleDeleteAll : handleDelete
                  }
                >
                  <Typography textTransform={"capitalize"}>Delete</Typography>
                </Button>
              </Grid>
            }
          </Grid>
        </Box>
      </Modal>

      <Modal
        open={openCancelModal}
        onClose={() => {
          setOpenCancelModal(false);
        }}
        aria-labelledby="delete-activity-modal"
        aria-describedby="delete-activity-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            boxShadow: 24,
            borderRadius: 2,
            backgroundColor: "#d0d0d0",
            color: "#000000",
            p: 3,
          }}
        >
          <Typography
            id="delete-activity-modal"
            variant="h4"
            mb={6}
            sx={{ color: "#000000" }}
          >
            Leave without saving?
          </Typography>
          <Typography
            id="delete-activity-modal"
            variant="h6"
            mb={6}
            sx={{ color: "#000000" }}
          >
            You’ll lose any changes you made to this activity.
          </Typography>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item>
              <Button
                variant="outlined"
                className="activity-action-btn"
                onClick={() => {
                  setOpenCancelModal(false);
                }}
              >
                <Typography textTransform={"capitalize"}> Go Back </Typography>
              </Button>
            </Grid>
            <Grid item>
              <Button variant="contained" color="error" onClick={handleLeave}>
                <Typography textTransform={"capitalize"}>Leave</Typography>
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>

      <Menu
        id="delete-options-menu"
        aria-labelledby="delete-options-button"
        anchorEl={anchorEl}
        open={deleteMenuOpen}
        onClose={handleCloseDeleteMenu}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <MenuItem
          onClick={() => {
            setDeleteTypeOption("single");
            // GMA-1228: record which hand-off the user clicked so QA (and
            // Sentry post-incident review) can correlate modal copy with
            // the eventual `deleteActivity(mode)` call.
            addBreadcrumb("activity.delete.modal_opened", {
              category: "activity.delete",
              level: "info",
              data: { ticket: "GMA-1228", mode: "single" },
            });
            handleOpen();
            setDeleteMenuOpen(false);
          }}
        >
          Delete Occurrence
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteTypeOption("all");
            addBreadcrumb("activity.delete.modal_opened", {
              category: "activity.delete",
              level: "info",
              data: { ticket: "GMA-1228", mode: "all" },
            });
            handleOpen();
            setDeleteMenuOpen(false);
          }}
        >
          Delete Series
        </MenuItem>
      </Menu>
    </>
  );
};

export default ActionBtn;
